import { type SortOrder, compareTimestamp } from '~/models/tableSort'
import type { ProcurementPlanRowWithBadge } from '~/pages/ProcurementPlanList/models/summary'

/** 列見出しクリックで並び替えられる列 (AC-PPL-012-1: 最終更新日時 / 提出日時)。 */
export type ProcurementPlanSortKey = 'lastUpdatedAt' | 'submittedAt'

/** 列見出しをクリックした後の並び替え状態。 `undefined` はデフォルト順 (backend の返却順)。 */
export type ProcurementPlanSort = {
  key: ProcurementPlanSortKey
  order: SortOrder
}

/**
 * 列見出しクリック後の並び替え状態 (AC-PPL-012-1)。
 *
 * 並んでいない列のクリックは昇順から始め、 並んでいる列のクリックは向きを反転する
 * (昇順 → 降順 → 昇順)。 デフォルト順 (`undefined`) へは戻さない。 8080 の
 * `table-sort.js` は 3 状態 (なし → 昇順 → 降順 → なし) だが、 spec の
 * 「デフォルト順への復帰なし」 を採る (#3380)。
 */
export function nextProcurementPlanSort(
  current: ProcurementPlanSort | undefined,
  clicked: ProcurementPlanSortKey,
): ProcurementPlanSort {
  if (current?.key !== clicked) return { key: clicked, order: 'asc' }
  return { key: clicked, order: current.order === 'asc' ? 'desc' : 'asc' }
}

/**
 * 一覧の行を列見出しの並び替え状態で並べる純関数 (AC-PPL-012-1)。
 *
 * - `sort` が `undefined` の時は入力順 (= デフォルト順の最終更新日時降順) をそのまま返す
 * - 値が無い行 (未提出の提出日時「—」等) は向きに関わらず末尾に置く。 降順で「—」 が
 *   先頭を占めないようにするためで、 業務ワークフロー一覧 (`sortWorkflowCases`) と同じ扱い
 * - 同値の行は入力順を保つ (Array.prototype.sort は安定ソート)
 */
export function sortProcurementPlanRows(
  rows: ProcurementPlanRowWithBadge[],
  sort: ProcurementPlanSort | undefined,
): ProcurementPlanRowWithBadge[] {
  if (sort === undefined) return rows
  const direction = sort.order === 'asc' ? 1 : -1
  return [...rows].sort((a, b) => {
    const va = a.row[sort.key]
    const vb = b.row[sort.key]
    if (va === undefined && vb === undefined) return 0
    if (va === undefined) return 1
    if (vb === undefined) return -1
    return compareTimestamp(va, vb) * direction
  })
}
